import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Train, ShoppingBag, School, Bus, GraduationCap, Building2, Calendar } from 'lucide-react';

const LocationSection = () => {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);

  const nearbyPlaces = [
    { 
      icon: <Bus className="w-5 h-5" />, 
      name: "Maduravoyal bus stand", 
      distance: "0.11 km", 
      category: "Transport",
      searchQuery: "Maduravoyal bus stand Chennai"
    },
    { 
      icon: <School className="w-5 h-5" />, 
      name: "Velammal Vidyalaya", 
      distance: "0.6 km", 
      category: "Education",
      searchQuery: "Velammal Vidyalaya Maduravoyal Chennai"
    },
    { 
      icon: <Building2 className="w-5 h-5" />, 
      name: "Ravina Hospital", 
      distance: "1.5 km", 
      category: "Healthcare",
      searchQuery: "Ravina Hospital Maduravoyal Chennai"
    },
    { 
      icon: <School className="w-5 h-5" />, 
      name: "The Schram Academy", 
      distance: "1.6 km", 
      category: "Education",
      searchQuery: "The Schram Academy Maduravoyal Chennai"
    },
    { 
      icon: <GraduationCap className="w-5 h-5" />, 
      name: "Dr. M.G.R. Educational Institute", 
      distance: "2.5 km", 
      category: "Education",
      searchQuery: "Dr MGR Educational Institute Chennai"
    },
    { 
      icon: <Train className="w-5 h-5" />, 
      name: "Valsaravakkam Metro Station", 
      distance: "3.5 km", 
      category: "Transport",
      searchQuery: "Valsaravakkam Metro Station Chennai"
    },
    { 
      icon: <Building2 className="w-5 h-5" />, 
      name: "Apollo Speciality Hospital", 
      distance: "4 km", 
      category: "Healthcare",
      searchQuery: "Apollo Speciality Hospital Vanagaram Chennai"
    },
    { 
      icon: <Building2 className="w-5 h-5" />, 
      name: "RMZ Software Park", 
      distance: "4.3 km", 
      category: "Business",
      searchQuery: "RMZ Software Park Porur Chennai"
    },
    { 
      icon: <ShoppingBag className="w-5 h-5" />, 
      name: "Nexus Vijaya Mall", 
      distance: "5 km", 
      category: "Shopping",
      searchQuery: "Nexus Vijaya Mall Vadapalani Chennai"
    },
    { 
      icon: <Bus className="w-5 h-5" />, 
      name: "Koyambedu Bus Terminus", 
      distance: "5.8 km", 
      category: "Transport",
      searchQuery: "Koyambedu Bus Terminus Chennai"
    }
  ];

  // Function to generate dynamic map URL with directions to Blucon Sunrise
  const getMapUrl = (searchQuery?: string) => {
  const bluconSunrisePlusCode = "353C+M5 Chennai, Tamil Nadu";
    
    if (searchQuery) {
      // Generate directions from selected location to Blucon Sunrise using plus code
      const encodedOrigin = encodeURIComponent(searchQuery);
      const encodedDestination = encodeURIComponent(bluconSunrisePlusCode);
      return `https://www.google.com/maps/dir/${encodedOrigin}/${encodedDestination}/@13.0399,80.1683,15z/data=!3m1!4b1?entry=ttu&output=embed`;
    } else {
      // Default to Blucon Sunrise location using plus code for precise pinning
      const encodedPlusCode = encodeURIComponent(bluconSunrisePlusCode);
      return `https://www.google.com/maps?q=${encodedPlusCode}&output=embed&z=17`;
    }
  };



  const resetToDefaultLocation = () => {
    setSelectedLocation(null);
  };

  return (
    <section id="location" className="py-20 bg-gradient-to-br from-teal-50 via-gray-50 to-teal-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4 bg-amber-100 text-teal-700">
            Prime Location
          </Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
           Location & Connectivity
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Perfectly positioned to connect you to the best of Chennai's work, life, and leisure.
          </p>
        </div>

        <div className="grid lg:grid-cols-[40%_60%] gap-12 items-start mb-12">
          {/* Left Content Section */}
          <div className="space-y-8">
            {/* Project Branding */}
            <div className="space-y-4">
              <h3 className="text-4xl lg:text-5xl font-bold text-amber-600 mb-2">
                Blucon Sunrise
              </h3>
              <div className="space-y-1">
                <p className="text-2xl font-semibold">
                  <span className="font-bold text-amber-600">53%</span>{' '}
                  <span className="font-bold text-teal-600">UDS</span>{' '}
                  <span className="text-purple-600">right inside city</span>
                </p>
                <p className="text-gray-600 text-lg">
                  more land, more value, more chennai.
                </p>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
              <MapPin className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">
                  Plot No 30 & 35, 20th St, Sri Krishna Nagar,
                </p>
                <p className="text-gray-700">
                  Alapakkam, Chennai - 600095
                </p>
              </div>
            </div>

            {/* Nearby Conveniences */}
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-gray-900">
                Nearby Conveniences
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Velammal Vidyalaya */}
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                    <School className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      Velammal Vidyalaya
                    </p>
                    <p className="text-xs text-gray-600">
                      4 mins drive
                    </p>
                  </div>
                </div>

                {/* The Schram Academy */}
                <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      The Schram Academy
                    </p>
                    <p className="text-xs text-gray-600">
                      6 mins drive
                    </p>
                  </div>
                </div>

                {/* VR Mall */}
                <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                    <ShoppingBag className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      VR Mall
                    </p>
                    <p className="text-xs text-gray-600">
                      15 mins drive
                    </p>
                  </div>
                </div>

                {/* DLF Cybercity */}
                <div className="flex items-center space-x-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      DLF Cybercity
                    </p>
                    <p className="text-xs text-gray-600">
                      15 mins drive
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Schedule Site Visit Button */}
            <div className="pt-4">
              <Button 
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-lg font-semibold text-lg flex items-center space-x-2 w-full md:w-auto"
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  contactSection?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <Calendar className="w-5 h-5" />
                <span>Schedule Your Site Visit</span>
              </Button>
            </div>
          </div>

          {/* Right Map Section */}
          <div>
            <Card className="border-0 shadow-xl shadow-amber-100/20 overflow-hidden rounded-lg">
              <CardContent className="p-0 relative" style={{ height: '540px', fontSize: 0, lineHeight: 0 }}>
                <iframe
                  key={selectedLocation || 'default'} // Force re-render when location changes
                  src={selectedLocation 
                    ? getMapUrl(nearbyPlaces.find(p => p.name === selectedLocation)?.searchQuery)
                    : getMapUrl()
                  }
                  width="100%"
                  height="100%"
                  style={{ 
                    border: 0, 
                    margin: 0, 
                    padding: 0, 
                    display: 'block', 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={selectedLocation 
                    ? `Directions from ${selectedLocation} to Blucon Sunrise` 
                    : "Blucon Sunrise Location - Plot No 30 & 35, 20th St, Sri Krishna Nagar, Alapakkam, Maduravoyal, Chennai"
                    }
                ></iframe>
                <div className="absolute top-4 left-4 space-y-2 z-10">
                  <Badge 
                    className={`text-white cursor-pointer hover:opacity-90 transition-all ${
                      selectedLocation ? 'bg-amber-600 animate-pulse' : 'bg-teal-600 hover:bg-teal-700'
                    }`}
                    onClick={resetToDefaultLocation}
                  >
                    <MapPin className="w-4 h-4 mr-1" />
                    {selectedLocation ? `Route from ${selectedLocation}` : "Blucon Sunrise Location"}
                  </Badge>
                  {selectedLocation && (
                    <div className="text-xs bg-black/70 text-white px-2 py-1 rounded flex items-center">
                      Showing directions • Click to reset
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Connectivity Section - Full Width Single Row */}
        <Card className="border-0 shadow-lg shadow-amber-100/20">

        </Card>
      </div>
    </section>
  );
};

export default LocationSection;